import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaInfoCircle } from 'react-icons/fa';
import RegistrationWizard from './RegistrationWizard';
import useRegistration from '../../hooks/useRegistration';
import { useUser } from '../../context/UserContext';
import { useNotification } from '../../context/NotificationContext';
import { getProgramMembers, enrollParticipant } from '../../api/programs';
import styles from './RegistrationButton.module.css';

const RegistrationButton = ({ program, onRegistrationComplete, clubId, onJoinClubRequired, showDetailsButton = true, showNotifications = false }) => {
  const navigate = useNavigate();
  const [showWizard, setShowWizard] = useState(false);
  const [membershipStatus, setMembershipStatus] = useState(null);
  const [membershipLoading, setMembershipLoading] = useState(true);
  const [notifiedApplicationIds, setNotifiedApplicationIds] = useState(new Set());
  const { userProfile, userClubs, getUserProgramIds } = useUser();
  const { showWarning, showInfo } = useNotification();

  
  const {
    config,
    applications,
    userApplications,
    loading,
    isRegistrationEnabled,
    isRegistrationOpen,
    isAtCapacity,
    registrationStatus,
    canUserApply,
    hasUserApplications,
    userApplicationsList,
    submitApplication,
    submitNewApplicationForWithdrawnUser,
    resubmitApplication
  } = useRegistration(program._id, program);

  // Get user's latest application if exists
  const userApplication = userApplicationsList && userApplicationsList.length > 0 
    ? userApplicationsList[0] 
    : null;

  // Check if user is a member of the club that owns this program
  const isClubMember = Array.isArray(userClubs) && userClubs.some(membership => {
    const membershipClubId = membership.club?._id || membership.club || membership.clubId;
    // Use clubId prop first, then fallback to program's clubId
    let targetClubId = clubId || program?.clubId || program?.club?._id || program?.club;
    
    // If targetClubId is an object, extract the _id
    if (typeof targetClubId === 'object' && targetClubId?._id) {
      targetClubId = targetClubId._id;
    }
    
    return String(membershipClubId) === String(targetClubId);
  });

  // Check user's current membership status and application permissions
  const [canApplyDespiteRestrictions, setCanApplyDespiteRestrictions] = useState(false);

  // Enhanced capacity checking using program.capacity first, fallback to config.capacity
  const isProgramAtCapacity = () => {
    // Priority 1: Use program.capacity (new capacity feature)
    if (program?.capacity?.maxParticipants && program?.capacity?.currentEnrolled !== undefined) {
      return program.capacity.currentEnrolled >= program.capacity.maxParticipants;
    }
    
    // Priority 2: Fallback to existing isAtCapacity logic from useRegistration
    return isAtCapacity();
  };

  // Enhanced waitlist checking using program.capacity first, fallback to config.capacity
  const isWaitlistEnabled = () => {
    // Priority 1: Use program.capacity (new capacity feature)
    if (program?.capacity?.waitlistEnabled !== undefined) {
      return program.capacity.waitlistEnabled;
    }
    
    // Priority 2: Fallback to config.capacity (existing registration system)
    return config?.capacity?.waitlistEnabled || false;
  };

  // Get enhanced registration status that considers new capacity feature
  const getEnhancedRegistrationStatus = () => {
    if (!isRegistrationEnabled) return 'disabled';
    if (!isRegistrationOpen) return 'closed';
    if (isProgramAtCapacity()) return 'full';
    return 'open';
  };
  
  useEffect(() => {
    const checkMembershipStatus = async () => {
      if (!program?._id || !userProfile?._id) {
        setMembershipLoading(false);
        return;
      }

      try {
        setMembershipLoading(true);
        const members = await getProgramMembers(program._id);
        const userMembership = Array.isArray(members) 
          ? members.find(m => (m.userId || m.user?._id) === userProfile._id)
          : null;
        
        setMembershipStatus(userMembership?.status || null);
        setCanApplyDespiteRestrictions(userMembership?.canApplyDespiteRestrictions || false);
      } catch (error) {
        console.error('Error checking membership status:', error);
        setMembershipStatus(null);
        setCanApplyDespiteRestrictions(false);
      } finally {
        setMembershipLoading(false);
      }
    };

    checkMembershipStatus();
  }, [program?._id, userProfile?._id]);

  // Show notification for non-club members
  useEffect(() => {
    if (!showNotifications) return;
    if (!isClubMember && userProfile && program) {
      showInfo(
        'You need to join this club first to register for programs. Click the "Join Club" button to get started.',
        { 
          duration: 6000,
          autoClose: true 
        }
      );
    }
  }, [showNotifications, isClubMember, userProfile, program, showInfo]);

  // Show notification when user is approved to register
  useEffect(() => {
    if (!showNotifications) return;
    if (canApplyDespiteRestrictions && !membershipLoading) {
      showInfo(
        "You've been approved to register! Click the 'Register for Program' button to complete your application.",
        { 
          duration: 8000,
          autoClose: true 
        }
      );
    }
  }, [showNotifications, canApplyDespiteRestrictions, membershipLoading, showInfo]);

  // Show notification for different membership statuses
  useEffect(() => {
    if (!showNotifications) return;
    if (!membershipLoading && hasUserApplications && userApplication?.status === 'approved' && userApplication?._id) {
      // Check if we've already shown notification for this application status
      const notificationKey = `${userApplication._id}-approved-${membershipStatus}`;
      if (notifiedApplicationIds.has(notificationKey)) {
        return;
      }

      let shouldShowNotification = false;
      let notificationMessage = '';
      let notificationType = 'info';

      // Withdrawn users
      if (membershipStatus === null || membershipStatus === 'Withdrawn' || membershipStatus === 'withdrawn') {
        shouldShowNotification = true;
        notificationMessage = 'Your registration was approved but you have been withdrawn from the program. You may apply again if needed.';
        notificationType = 'warning';
      }
      // Suspended users
      else if (membershipStatus === 'Suspended' || membershipStatus === 'suspended') {
        shouldShowNotification = true;
        notificationMessage = 'Your membership has been suspended. Please contact the program administrators for more information.';
        notificationType = 'warning';
      }
      // Enrolled users
      else if (membershipStatus === 'Enrolled' || membershipStatus === 'enrolled') {
        shouldShowNotification = true;
        notificationMessage = '🎉 Congratulations! Your application has been approved and you are now enrolled in the program.';
        notificationType = 'info';
      }
      // Default approved case (still in applicants processing)
      // Only show this for users who are NOT waitlisted
      else if (membershipStatus !== 'Waitlisted' && membershipStatus !== 'waitlisted') {
        shouldShowNotification = true;
        notificationMessage = 'Registration approved! Your application has been moved to the applicants list for final processing.';
        notificationType = 'info';
      }

      if (shouldShowNotification) {
        const showNotification = notificationType === 'warning' ? showWarning : showInfo;
        showNotification(notificationMessage, { 
          duration: 8000,
          autoClose: true 
        });
        
        // Mark this application status as notified
        setNotifiedApplicationIds(prev => new Set([...prev, notificationKey]));
      }
    }
  }, [showNotifications, membershipLoading, membershipStatus, hasUserApplications, userApplication?.status, userApplication?._id, notifiedApplicationIds, showWarning, showInfo]);

  // Show notification when application is submitted
  useEffect(() => {
    if (!showNotifications) return;
    if (!membershipLoading && hasUserApplications && userApplication?.status === 'submitted' && userApplication?._id) {
      // Check if we've already shown notification for this application
      if (notifiedApplicationIds.has(userApplication._id)) {
        return;
      }

      const formatSubmittedDate = (application) => {
        // Try multiple date fields in order of preference
        const dateFields = [
          application.submittedAt,
          application.createdAt,
          application.updatedAt,
          application._id ? new Date(parseInt(application._id.substring(0, 8), 16) * 1000) : null // Extract date from MongoDB ObjectId
        ];
        
        for (const dateField of dateFields) {
          if (dateField) {
            const date = new Date(dateField);
            if (!isNaN(date.getTime())) {
              return date.toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric'
              });
            }
          }
        }
        
        return 'recently'; // Final fallback
      };

      // Add a small delay to ensure this notification shows after the wizard success notification
      const timer = setTimeout(() => {
        showInfo(
          `Application submitted ${formatSubmittedDate(userApplication)}. Your application is being reviewed.`,
          { 
            duration: 6000,
            autoClose: true 
          }
        );
        
        // Mark this application as notified
        setNotifiedApplicationIds(prev => new Set([...prev, userApplication._id]));
      }, 1000); // 1 second delay

      return () => clearTimeout(timer);
    }
  }, [showNotifications, membershipLoading, hasUserApplications, userApplication?.status, userApplication?._id, userApplication?.submittedAt, userApplication?.createdAt, notifiedApplicationIds, showInfo]);

  // Show notification when application is rejected
  useEffect(() => {
    if (!showNotifications) return;
    if (!membershipLoading && hasUserApplications && userApplication?.status === 'rejected' && userApplication?._id) {
      // Check if we've already shown notification for this application
      const notificationKey = `${userApplication._id}-rejected`;
      if (notifiedApplicationIds.has(notificationKey)) {
        return;
      }

      showWarning(
        'Your application has been rejected. You may submit a new application if you wish to reapply.',
        { 
          duration: 8000,
          autoClose: true 
        }
      );
      
      // Mark this application as notified
      setNotifiedApplicationIds(prev => new Set([...prev, notificationKey]));
    }
  }, [showNotifications, membershipLoading, hasUserApplications, userApplication?.status, userApplication?._id, notifiedApplicationIds, showWarning]);

  // Show notification when application is waitlisted
  useEffect(() => {
    if (!showNotifications) return;
    if (!membershipLoading && hasUserApplications && userApplication?.status === 'waitlisted' && userApplication?._id) {
      // Check if we've already shown notification for this application
      const notificationKey = `${userApplication._id}-waitlisted`;
      if (notifiedApplicationIds.has(notificationKey)) {
        return;
      }

      showInfo(
        'Your application has been placed on the waitlist. You will be notified if a spot becomes available.',
        { 
          duration: 8000,
          autoClose: true 
        }
      );
      
      // Mark this application as notified
      setNotifiedApplicationIds(prev => new Set([...prev, notificationKey]));
    }
  }, [showNotifications, membershipLoading, hasUserApplications, userApplication?.status, userApplication?._id, notifiedApplicationIds, showInfo]);

  // Show notification for users with various membership statuses
  useEffect(() => {
    if (!showNotifications) return;
    if (!membershipLoading && membershipStatus && userProfile && program) {
      // Check if we've already shown notification for this program and status
      const notificationKey = `${program._id}-${membershipStatus}-status`;
      if (notifiedApplicationIds.has(notificationKey)) {
        return;
      }

      let shouldShowNotification = false;
      let notificationMessage = '';
      let notificationType = 'info';

      switch (membershipStatus) {
        case 'Applied':
          shouldShowNotification = true;
          notificationMessage = 'You have already applied for this program. Your application is currently being reviewed by the program administrators.';
          notificationType = 'info';
          break;
        case 'Enrolled':
          shouldShowNotification = true;
          notificationMessage = '🎉 Congratulations! You are enrolled in this program. Check the program details for schedule and requirements.';
          notificationType = 'info';
          break;
        case 'Waitlisted':
          shouldShowNotification = true;
          notificationMessage = 'You are currently on the waitlist for this program. You will be notified if a spot becomes available.';
          notificationType = 'info';
          break;
        case 'Declined':
          shouldShowNotification = true;
          notificationMessage = 'Your application for this program was declined. You may be able to reapply if registration reopens.';
          notificationType = 'warning';
          break;
        case 'Withdrawn':
          shouldShowNotification = true;
          notificationMessage = 'You have withdrawn from this program. You may be able to reapply if registration is available.';
          notificationType = 'warning';
          break;
        case 'Suspended':
          shouldShowNotification = true;
          notificationMessage = 'Your membership in this program has been suspended. Please contact the program administrators for more information.';
          notificationType = 'warning';
          break;
        case 'Following':
          // Don't show notification for following status - it's just interest
          break;
        default:
          // Don't show notification for unknown statuses
          break;
      }

      if (shouldShowNotification) {
        const showNotification = notificationType === 'warning' ? showWarning : showInfo;
        showNotification(notificationMessage, { 
          duration: 8000,
          autoClose: true 
        });
        
        // Mark this program and status as notified
        setNotifiedApplicationIds(prev => new Set([...prev, notificationKey]));
      }
    }
  }, [showNotifications, membershipLoading, membershipStatus, userProfile, program, notifiedApplicationIds, showInfo, showWarning]);

  const handleRegistrationSubmit = async (applicationData) => {
    try {
      let result;
      
      // Check if this is a user who needs to create a new application
      const needsNewApplication = 
        // Withdrawn user applying again
        (hasUserApplications && 
         userApplication?.status === 'approved' && 
         !membershipLoading && 
         (membershipStatus === null || membershipStatus === 'Withdrawn' || membershipStatus === 'withdrawn')) ||
        // Waitlisted user who has been granted permission to apply
        canApplyDespiteRestrictions;
      
      if (needsNewApplication) {
        // Use special submission method for users who need a new application
        result = await submitNewApplicationForWithdrawnUser(applicationData);
      } else {
        // Use regular submission method
        result = await submitApplication(applicationData);
      }
      
      setShowWizard(false);
      
      // Handle resubmission response
      if (result?.isResubmission) {
        // Optional: Could show a specific message for resubmissions
      }
      
      if (onRegistrationComplete) {
        onRegistrationComplete();
      }
      // Note: Success message will be shown by RegistrationWizard
      return result;
    } catch (error) {
      // Note: Error message will be shown by RegistrationWizard
      throw error;
    }
  };

  const handleResubmit = async (applicationId) => {
    try {
      await resubmitApplication(applicationId);
      // After resubmit, open the wizard to allow editing
      setShowWizard(true);
    } catch (error) {
      console.error('Error resubmitting application:', error);
      // Could add error notification here
    }
  };

  const getButtonText = () => {
    if (loading) return 'Loading...';
    
    // If user is not a club member, show join club button
    if (!isClubMember) {
      return 'Join Club';
    }
    
    // If user can apply despite restrictions (from waitlist), show register button
    // But only if they haven't already submitted an application
    if (canApplyDespiteRestrictions && !hasUserApplications) {
      return 'Register for Program';
    }
    
    // If registration is disabled, show join button
    if (!isRegistrationEnabled) {
      return 'Join Program';
    }

    // If user has applications, show status-based text
    if (hasUserApplications && userApplication) {
      switch (userApplication.status) {
        case 'submitted':
        case 'under_review':
          return 'Application Pending';
        case 'documents_pending':
          return 'Documents Required';
        case 'approved':
          // Check if user has been withdrawn/removed from the program
          if (!membershipLoading && (membershipStatus === null || membershipStatus === 'Withdrawn' || membershipStatus === 'withdrawn')) {
            return 'Apply Again';
          }
          
          // Check if user is suspended
          if (membershipStatus === 'Suspended' || membershipStatus === 'suspended') {
            return 'Membership Suspended';
          }
          
          // Check if user is waitlisted
          if (membershipStatus === 'Waitlisted' || membershipStatus === 'waitlisted') {
            return 'On Waitlist';
          }
          
          // Check if user is enrolled
          if (membershipStatus === 'Enrolled' || membershipStatus === 'enrolled') {
            return 'Enrolled in Program';
          }
          
          // Default approved state (still in process)
          return 'Registration Approved - Awaiting Final Approval';
        case 'rejected':
          return 'Apply Again';
        case 'waitlisted':
          return 'On Waitlist';
        case 'draft':
          // Check if this was previously rejected
          if (userApplication.adminNotes?.some(note => note.isFromPreviousSubmission)) {
            return 'Continue Resubmission';
          }
          return 'Continue Registration';
        default:
          return 'View Application';
      }
    }

    // Check program membership status if no registration application exists
    if (!membershipLoading && membershipStatus) {
      const normalizedStatus = membershipStatus.toLowerCase();
      switch (normalizedStatus) {
        case 'applied':
          return 'Application Pending';
        case 'enrolled':
          return 'Enrolled in Program';
        case 'waitlisted':
          return 'On Waitlist';
        case 'declined':
          return 'Application Declined';
        case 'withdrawn':
          return 'Withdrawn from Program';
        case 'suspended':
          return 'Membership Suspended';
        case 'following':
          // Continue to normal flow for following users
          break;
        default:
          // Continue to normal flow for unknown statuses
          break;
      }
    }

    // If user cannot apply (e.g., already has active application)
    if (!canUserApply) {
      return 'Application In Progress';
    }

    // Enhanced registration status-based text using new capacity feature
    const enhancedStatus = getEnhancedRegistrationStatus();
    switch (enhancedStatus) {
      case 'disabled':
        return 'Join Program';
      case 'closed':
        return 'Registration Closed';
      case 'full':
        return isWaitlistEnabled() ? 'Join Waitlist' : 'Program Full';
      case 'open':
        return 'Register for Program';
      default:
        return 'Register for Program';
    }
  };

  const getButtonClass = () => {
    if (canApplyDespiteRestrictions && !hasUserApplications) {
      return `${styles.registrationButton} ${styles.primary}`;
    }

    const enhancedStatus = getEnhancedRegistrationStatus();
    
    if (loading || !isClubMember || enhancedStatus === 'closed' || 
        (enhancedStatus === 'full' && !isWaitlistEnabled())) {
      return `${styles.registrationButton} ${styles.disabled}`;
    }

    // Special styling for waitlist button
    if (enhancedStatus === 'full' && isWaitlistEnabled()) {
      return `${styles.registrationButton} ${styles.waitlist}`;
    }

    if (userApplication) {
      switch (userApplication.status) {
        case 'submitted':
        case 'under_review':
        case 'documents_pending':
          return `${styles.registrationButton} ${styles.pending}`;
        case 'approved':
          return `${styles.registrationButton} ${styles.approved}`;
        case 'rejected':
          return `${styles.registrationButton} ${styles.rejected}`;
        case 'waitlisted':
          return `${styles.registrationButton} ${styles.waitlisted}`;
        default:
          return `${styles.registrationButton} ${styles.primary}`;
      }
    }

    // Handle program membership status styling
    if (!membershipLoading && membershipStatus) {
      const normalizedStatus = membershipStatus.toLowerCase();
      switch (normalizedStatus) {
        case 'applied':
          return `${styles.registrationButton} ${styles.pending}`;
        case 'enrolled':
          return `${styles.registrationButton} ${styles.approved}`;
        case 'waitlisted':
          return `${styles.registrationButton} ${styles.waitlisted}`;
        case 'declined':
        case 'withdrawn':
        case 'suspended':
          return `${styles.registrationButton} ${styles.rejected}`;
        default:
          break;
      }
    }

    return `${styles.registrationButton} ${styles.primary}`;
  };

  const isButtonDisabled = () => {
    if (loading || membershipLoading) return true;
    
    // Enable button if user is not a club member (so they can join)
    if (!isClubMember) return false;
    
    // If user can apply despite restrictions, enable the button
    // But only if they haven't already submitted an application
    if (canApplyDespiteRestrictions && !hasUserApplications) return false;
    
    // If user has applications, check their status
    if (hasUserApplications && userApplication) {
      // For approved applications, check membership status
      if (userApplication.status === 'approved') {
        // If user has been withdrawn/removed, enable the button so they can apply again
        if (membershipStatus === null || membershipStatus === 'Withdrawn' || membershipStatus === 'withdrawn') {
          return false;
        }
        // If user is suspended, disable the button
        if (membershipStatus === 'Suspended' || membershipStatus === 'suspended') {
          return true;
        }
        // If user is waitlisted, disable the button
        if (membershipStatus === 'Waitlisted' || membershipStatus === 'waitlisted') {
          return true;
        }
        // If user is enrolled, disable the button
        if (membershipStatus === 'Enrolled' || membershipStatus === 'enrolled') {
          return true;
        }
        // Default: disable for approved applications (awaiting final approval)
        return true;
      }
      
      // For other statuses
      return ['submitted', 'under_review', 'waitlisted'].includes(userApplication.status);
    }

    // Handle program membership status disabled states
    if (!membershipLoading && membershipStatus) {
      const normalizedStatus = membershipStatus.toLowerCase();
      switch (normalizedStatus) {
        case 'applied':
        case 'enrolled':
        case 'waitlisted':
        case 'suspended':
          return true; // These statuses should have disabled buttons
        case 'declined':
        case 'withdrawn':
          return false; // These users might be able to reapply
        case 'following':
          // Continue to normal flow
          break;
        default:
          // Continue to normal flow
          break;
      }
    }

    // If user cannot apply for other reasons
    if (!canUserApply && isRegistrationEnabled) {
      return true;
    }

    const enhancedStatus = getEnhancedRegistrationStatus();
    return enhancedStatus === 'closed' || 
           (enhancedStatus === 'full' && !isWaitlistEnabled());
  };

  const handleButtonClick = async () => {
    if (isButtonDisabled()) return;
    
    // If user is not a club member, show join club modal
    if (!isClubMember) {
      // We need to trigger the join club functionality
      // This will be handled by the parent component
      if (onJoinClubRequired) {
        onJoinClubRequired();
      }
      return;
    }

    // If user can apply despite restrictions and hasn't applied yet, show registration wizard
    if (canApplyDespiteRestrictions && !hasUserApplications) {
      setShowWizard(true);
      return;
    }

    // If registration is disabled, send join request to club management
    if (!isRegistrationEnabled) {
      try {
        showInfo('Sending join request to program management...', { duration: 2000 });
        
        // Enroll participant with 'direct_join' source to indicate no registration process
        await enrollParticipant(program._id, userProfile._id, 'direct_join');
        
        showInfo(
          '✅ Join request sent successfully! The program management will review your request.',
          { duration: 5000 }
        );
        
        if (onRegistrationComplete) {
          onRegistrationComplete();
        }
      } catch (error) {
        console.error('Error sending join request:', error);
        showWarning(
          error.message || 'Failed to send join request. Please try again or contact support.',
          { duration: 5000 }
        );
      }
      return;
    }

    // If user has approved application, check their membership status
    if (hasUserApplications && userApplication?.status === 'approved') {
      // If user has been withdrawn/removed, allow them to apply again
      if (!membershipLoading && (membershipStatus === null || membershipStatus === 'Withdrawn' || membershipStatus === 'withdrawn')) {
        setShowWizard(true);
        return;
      }
      
      // If user is suspended, no action allowed
      if (membershipStatus === 'Suspended' || membershipStatus === 'suspended') {
        return;
      }
      
      // If user is waitlisted, no action allowed
      if (membershipStatus === 'Waitlisted' || membershipStatus === 'waitlisted') {
        return;
      }
      
      // If user is enrolled, no action needed
      if (membershipStatus === 'Enrolled' || membershipStatus === 'enrolled') {
        return;
      }
      
      // Default: Don't allow any action - user needs to wait for final approval in applicants tab
      return;
    }

    // If user has a draft or rejected application, continue/restart registration
    if (hasUserApplications && userApplication && 
        ['draft', 'rejected'].includes(userApplication.status)) {
      setShowWizard(true);
      return;
    }

    // If user can apply, show registration wizard
    if (canUserApply) {
      setShowWizard(true);
      return;
    }

    // Fallback to simple join for edge cases
    if (onRegistrationComplete) {
      onRegistrationComplete();
    }
  };

  const getStatusMessage = () => {
    
    if (!isRegistrationEnabled) return null;

    if (hasUserApplications && userApplication) {
      switch (userApplication.status) {
        case 'submitted':
          return null;
        case 'under_review':
          return (
            <div className={styles.statusMessage}>
              <span className={styles.statusIcon}>👀</span>
              Application under review
            </div>
          );
        case 'documents_pending':
          return (
            <div className={styles.statusMessage}>
              <span className={styles.statusIcon}>📎</span>
              Please upload required documents
            </div>
          );
        case 'approved':
          // Check if user has been withdrawn/removed from the program
          if (!membershipLoading && (membershipStatus === null || membershipStatus === 'Withdrawn' || membershipStatus === 'withdrawn')) {
            // Don't show inline message - notification will be shown instead
            return null;
          }
          
          // Check if user is waitlisted
          if (membershipStatus === 'Waitlisted' || membershipStatus === 'waitlisted') {
            return null;
          }
          
          // Check if user is enrolled
          if (membershipStatus === 'Enrolled' || membershipStatus === 'enrolled') {
            return (
              <div className={styles.statusMessage}>
                <span className={styles.statusIcon}>🎉</span>
                Congratulations! You are enrolled in the program.
              </div>
            );
          }
          
          // Default approved message (notification will be shown instead)
          return null;
        case 'rejected':
          return (
            <div className={styles.statusMessage}>
              <span className={styles.statusIcon}>❌</span>
              Application rejected. You may reapply.
            </div>
          );
        case 'waitlisted':
          return null;
        default:
          return null;
      }
    }

    // Show status message for program membership status if no registration application
    if (!membershipLoading && membershipStatus && !hasUserApplications) {
      const normalizedStatus = membershipStatus.toLowerCase();
      switch (normalizedStatus) {
        case 'applied':
          return (
            <div className={styles.statusMessage}>
              <span className={styles.statusIcon}>⏳</span>
              Your application is being reviewed
            </div>
          );
        case 'waitlisted':
          return (
            <div className={styles.statusMessage}>
              <span className={styles.statusIcon}>⏰</span>
              You are on the waitlist for this program
            </div>
          );
        case 'declined':
          return (
            <div className={styles.statusMessage}>
              <span className={styles.statusIcon}>❌</span>
              Application declined. You may reapply if available.
            </div>
          );
        case 'withdrawn':
          return (
            <div className={styles.statusMessage}>
              <span className={styles.statusIcon}>↩️</span>
              You have withdrawn from this program
            </div>
          );
        case 'suspended':
          return (
            <div className={styles.statusMessage}>
              <span className={styles.statusIcon}>⚠️</span>
              Your membership has been suspended
            </div>
          );
        default:
          break;
      }
    }

    const enhancedStatus = getEnhancedRegistrationStatus();
    
    if (enhancedStatus === 'closed') {
      return (
        <div className={styles.statusMessage}>
          <span className={styles.statusIcon}>🔒</span>
          Registration period has ended
        </div>
      );
    }

    if (enhancedStatus === 'full') {
      return (
        <div className={styles.statusMessage}>
          <span className={styles.statusIcon}>👥</span>
          Program is at capacity
          {isWaitlistEnabled() && ' - Waitlist available'}
        </div>
      );
    }

    if (config?.registrationPeriod?.endDate) {
      const endDate = new Date(config.registrationPeriod.endDate);
      const now = new Date();
      const daysLeft = Math.ceil((endDate - now) / (1000 * 60 * 60 * 24));
      
      if (daysLeft > 0 && daysLeft <= 7) {
        return (
          <div className={styles.statusMessage}>
            <span className={styles.statusIcon}>⏰</span>
            Registration closes in {daysLeft} day{daysLeft !== 1 ? 's' : ''}
          </div>
        );
      }
    }

    return null;
  };

  // Navigate to program details page
  const handleViewDetails = () => {
    const targetClubId = clubId || program?.clubId?._id || program?.clubId || program?.club?._id || program?.club;
    if (targetClubId && program?._id) {
      navigate(`/clubs/${targetClubId}/programs/${program._id}`);
    }
  };

  return (
    <div className={styles.registrationButtonContainer}>
      <div className={styles.buttonRow}>
        <button
          className={getButtonClass()}
          onClick={handleButtonClick}
          disabled={isButtonDisabled()}
        >
          {getButtonText()}
        </button>
        
        {showDetailsButton && (
          <button
            className={styles.detailsButton}
            onClick={handleViewDetails}
            title="View Program Details"
          >
            <FaInfoCircle />
            <span>Details</span>
          </button>
        )}
      </div>
      
      {getStatusMessage()}

      {/* Registration Wizard Modal */}
      {showWizard && (
        <RegistrationWizard
          programId={program._id}
          config={config}
          template={config?.template || config?.templateId}
          existingApplication={userApplication}
          onClose={() => setShowWizard(false)}
          onSubmit={handleRegistrationSubmit}
        />
      )}
    </div>
  );
};

export default RegistrationButton;
